import { isDefined } from "@clipboard-health/util-ts";
import { API_TYPES, get } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import {
  type QueryFunctionContext,
  type QueryKey,
  useInfiniteQuery,
  type UseInfiniteQueryOptions,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { isEmpty } from "lodash";
import { useHistory } from "react-router-dom";
import { type z } from "zod";

import {
  type CandidateBanInclude,
  type GetPlacementsForWorkplaceQuery,
  getPlacementsForWorkplaceQuerySchema,
  type GetPlacementsResponse,
  paginatedGetPlacementsResponseSchema,
  type placementDataSchema,
  type PlacementIncludedApplication,
  type PlacementIncludedInterview,
  type PlacementIncludedInterviewSettings,
  type PlacementIncludedWorkplace,
  type RequirementInclude,
  type TagInclude,
  type WorkerRequirementInclude,
  type WorkplaceUserInclude,
} from "../types/fetchPlacements.schema";

export function fetchPlacementsApiUrl(workerId: string) {
  return `${environmentConfig.REACT_APP_BASE_API_URL}/workers/${workerId}/placements`;
}

export interface FetchPlacementsParams {
  workerId: string;
  placementCandidateId: string;
  filter?: GetPlacementsForWorkplaceQuery["filter"];
  shouldLogEvent: boolean;
  sort?: GetPlacementsForWorkplaceQuery["sort"];
  pageSize?: number;
}

export type WorkerRequirement = WorkerRequirementInclude & {
  relationships: {
    requirement: { data: RequirementInclude };
  };
};

export type PlacementWithDetails = z.infer<typeof placementDataSchema.shape.attributes> &
  z.infer<typeof placementDataSchema.shape.meta> & {
    id: string;
    workplace: PlacementIncludedWorkplace["attributes"] &
      PlacementIncludedWorkplace["meta"] & {
        id: string;
      };
    applications: PlacementIncludedApplication[] | undefined;
    interviewSettings: PlacementIncludedInterviewSettings | undefined;
    interviews: PlacementIncludedInterview[];
    workerRequirements: WorkerRequirement[] | undefined;
    credentialRequirements: WorkerRequirement[] | undefined;
    workerFavoritePlacementId?: string;
    tags: TagInclude[] | undefined;
    candidateBans: CandidateBanInclude[] | undefined;
    hiringManagers: Array<WorkplaceUserInclude & { isPrimary: boolean }> | undefined;
  };

export function useFetchPaginatedPlacements(
  params: FetchPlacementsParams,
  options: UseInfiniteQueryOptions<GetPlacementsResponse, AxiosError> = {}
) {
  const { workerId, placementCandidateId, filter, shouldLogEvent, sort, pageSize = 20 } = params;
  const history = useHistory();
  const searchParams = new URLSearchParams(history.location.search);
  const source = searchParams.get("source");

  return useInfiniteQuery({
    queryKey: [fetchPlacementsApiUrl(workerId), filter, source, sort],
    queryFn: async ({
      pageParam: pageParameter = undefined,
    }: QueryFunctionContext<QueryKey, string | undefined>) => {
      const nonEmptyFilter = Object.fromEntries(
        Object.entries(filter ?? {}).filter(
          ([_, value]) => isDefined(value) && (typeof value !== "string" || value !== "")
        )
      );

      const requestUrl = fetchPlacementsApiUrl(workerId);
      const queryParams = {
        page: {
          size: pageSize,
          cursor: pageParameter,
        },
        filter: nonEmptyFilter,
        ...(isDefined(sort) ? { sort } : {}),
      };

      const response = await get({
        url: requestUrl,
        queryParams,
        responseSchema: paginatedGetPlacementsResponseSchema,
        requestSchema: getPlacementsForWorkplaceQuerySchema,
      });

      if (!isEmpty(placementCandidateId) && shouldLogEvent) {
        logEvent(APP_V2_APP_EVENTS.PLACEMENTS_PAGE_VIEWED, {
          placementCandidateId,
          filter: nonEmptyFilter,
          source,
        });
      }

      return response.data;
    },
    getNextPageParam: (lastPage) => lastPage.links.nextCursor ?? undefined,
    ...options,
  });
}

export function transformResponseIntoPlacement(
  placement: z.infer<typeof placementDataSchema>,
  placementIncludedMetaData: GetPlacementsResponse["included"],
  credentialRequirementConfiguration: Set<string>
): PlacementWithDetails {
  const workplace = placementIncludedMetaData.find(
    (included): included is PlacementIncludedWorkplace =>
      included.type === API_TYPES.workplace &&
      included.id === placement.relationships.workplace.data.id
  );

  if (!workplace) {
    throw new Error("Workplace details not found");
  }

  const interviewSettings = placementIncludedMetaData.find(
    (included): included is PlacementIncludedInterviewSettings =>
      included.type === API_TYPES.interviewSettings &&
      included.relationships.workplace.data.id === workplace.id
  );

  const applications = placement.relationships.applications?.data.map((application) => {
    const applicationData = placementIncludedMetaData.find(
      (included): included is PlacementIncludedApplication =>
        included.type === API_TYPES.placementApplication && included.id === application.id
    );
    if (!applicationData) {
      throw new Error("Application details not found");
    }

    return applicationData;
  });

  const interviews = placementIncludedMetaData.filter(
    (included): included is PlacementIncludedInterview =>
      included.type === API_TYPES.interview &&
      (placement.relationships.applications?.data.some(
        (application) => application.id === included.relationships.placementApplication.data.id
      ) ??
        false)
  );

  // Extract worker requirements
  const allWorkerRequirements = placementIncludedMetaData
    .filter(
      (included): included is WorkerRequirementInclude =>
        included.type === API_TYPES.workerRequirement &&
        workplace.relationships.workerRequirements.data.some(
          (workerRequirement) => workerRequirement.id === included.id
        ) &&
        included.attributes.qualifications.some((qualification) =>
          placement.attributes.workerTypes?.includes(qualification)
        )
    )
    .map((workerRequirement) => {
      const requirement = placementIncludedMetaData.find(
        (included): included is RequirementInclude =>
          included.type === API_TYPES.requirement &&
          included.id === workerRequirement.relationships.requirement.data.id
      );
      if (!requirement) {
        throw new Error("Requirement details not found");
      }

      return {
        ...workerRequirement,
        relationships: {
          ...workerRequirement.relationships,
          requirement: {
            data: {
              ...workerRequirement.relationships.requirement.data,
              attributes: requirement?.attributes,
            },
          },
        },
      };
    });

  const workerRequirements = placement.meta.isExemptFromDocumentRequirements
    ? []
    : allWorkerRequirements.filter(
        (workerRequirement) =>
          !credentialRequirementConfiguration.has(
            workerRequirement.relationships.requirement.data.id
          )
      );

  const credentialRequirements = allWorkerRequirements.filter((workerRequirement) =>
    credentialRequirementConfiguration.has(workerRequirement.relationships.requirement.data.id)
  );

  // Extract tags
  const tags = placement.relationships.tags?.data
    .map((tagRef) => {
      const tagData = placementIncludedMetaData.find(
        (included): included is TagInclude =>
          included.type === API_TYPES.tag && included.id === tagRef.id
      );
      if (!tagData) {
        return undefined;
      }

      return tagData;
    })
    .filter((tag): tag is TagInclude => tag !== undefined);

  const candidateBans = placementIncludedMetaData.filter(
    (included): included is CandidateBanInclude => included.type === API_TYPES.placementCandidateBan
  );

  const hiringManagers = (placement.relationships.hiringManagers?.data ?? [])
    .map((hiringManagerRelationship) => {
      const data = placementIncludedMetaData.find(
        (placementIncluded): placementIncluded is WorkplaceUserInclude =>
          placementIncluded.type === API_TYPES.workplaceUser &&
          placementIncluded.id === hiringManagerRelationship.id
      );
      return data
        ? { ...data, isPrimary: hiringManagerRelationship.meta?.isPrimary ?? false }
        : undefined;
    })
    .filter(
      (workplaceUser): workplaceUser is WorkplaceUserInclude & { isPrimary: boolean } =>
        workplaceUser !== undefined
    );

  return {
    ...placement.attributes,
    ...placement.meta,
    id: placement.id,
    workplace: { ...workplace.attributes, id: workplace.id },
    applications,
    interviewSettings,
    interviews,
    workerRequirements,
    credentialRequirements,
    tags,
    candidateBans,
    hiringManagers,
    workerFavoritePlacementId: placement.relationships.workerFavoritePlacement?.data?.id,
  };
}
